import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import type { ColumnErrors, ColumnMapping, FileMapping, FileUploadMetadata } from 'venn-api';
import { type DropMenuCheckboxItem, GetColor } from 'venn-ui-kit';
import { ErrorMessage } from '../../components/page-parts';

import {
  additionalMetadata,
  type ColumnMappingMetadata,
  convertColumnErrorsToViewModels,
  determineFormat,
  determineOwnershipState,
  type ErrorViewModel,
  type FormatType,
  getColumnsToUpload,
  type ReviewCellRendererProps,
  updateColumnMappingsForNewReviewByGlobalChange,
  updateMappingColumnStateForNewReview,
  updateErrorValue,
  validateCells,
} from './helpers';
import { fetchCorrectData } from '../../fetchers';
import Context from '../../context';
import { useHasFF } from 'venn-utils';
import { UniversalUploaderFooter } from '../../components/page-parts/UniversalUploaderFooter';
import { cn } from '@/lib/utils';
import { DataGrid } from '../../../../data-grid';
import {
  ActionCellRenderer,
  CurrencyCellRenderer,
  ErrorCellRenderer,
  ErrorsListRenderer,
  NameCellRenderer,
  OwnershipCellRenderer,
  SparklineWithFormatAndPerformanceCellRenderer,
  StatusCellRenderer,
} from './renderers';

import type { ColDef, GetRowIdParams, RowSelectionOptions } from 'ag-grid-community';
import { type BulkEditableProps, BulkInvestmentEditor } from './BulkInvestmentEditor';
import type { ExistingOwnerships, OwnershipSelectorState } from '../../InvestmentDataUploader';
import UserContext from '../../../../contexts/user-context';
import { getContextMenuItems } from '../../../../utils/getContextMenuItems';
import { useIsPrice } from '../hooks/useIsPrice';

export interface ReviewProps {
  metadata: FileUploadMetadata;
  mapping: FileMapping;
  errorColumns?: ColumnErrors[];
  onMappingChange: (newMapping: FileMapping) => void;
  updateMappingOptimistically: (newMapping: FileMapping) => void;
  onCancel: () => void;
  onStartOver: () => void;
  onContinue: (mapping: FileMapping, errors: ErrorViewModel[]) => void;
  loading: boolean;
  isSample: boolean;
  /**
   * Custom text to display on the Upload Review screen
   */
  customUploadReviewButtonLabel?: string;
  ownershipSelectorState: OwnershipSelectorState;
  setOwnershipSelectorState: React.Dispatch<React.SetStateAction<OwnershipSelectorState>>;
  existingOwnerships: ExistingOwnerships;
  setExistingOwnerships: React.Dispatch<React.SetStateAction<ExistingOwnerships>>;
}

const rowSelection: RowSelectionOptions = {
  mode: 'multiRow',
  selectAll: 'currentPage',
};

const DEFAULT_COL_DEF = {
  resizable: false,
  lockPinned: true,
  suppressHeaderMenuButton: true,
  suppressMovable: true,
  editable: false,
  headerClass: 'ag-left-aligned-header',
};

export const NewReview = ({
  metadata,
  mapping,
  errorColumns,
  onMappingChange,
  onCancel,
  onStartOver,
  onContinue,
  loading,
  isSample,
  customUploadReviewButtonLabel,
  ownershipSelectorState,
  setOwnershipSelectorState,
  existingOwnerships,
  setExistingOwnerships,
  updateMappingOptimistically,
}: ReviewProps) => {
  const hasInvestmentOwnership = useHasFF('investment_ownership_ff');
  const context = useContext(Context);
  const { mode } = context;

  const [error, setError] = useState<string | React.ReactNode>(context.error);
  const [errors, setErrors] = useState<ErrorViewModel[]>(convertColumnErrorsToViewModels(errorColumns ?? []));
  const [editingSeriesId, setEditingSeriesId] = useState<string | undefined>(undefined);
  const [currentMapping, setCurrentMapping] = useState<FileMapping>(mapping);
  const [selectedRows, setSelectedRows] = useState<ColumnMapping[]>([]);
  const [bulkEditableProps, setBulkEditableProps] = useState<BulkEditableProps>({
    selectedFormats: new Set(),
    selectedCurrencies: new Set(),
    selectedOwnerships: [],
  });

  const { profileSettings, currentContext } = useContext(UserContext);
  const availableContexts = profileSettings?.availableContexts;
  const availableOwnerships = getContextMenuItems(availableContexts, currentContext, 'CREATE_FUND');

  const isPrice = useIsPrice({ metadata, mapping, editingSeriesId, mode });

  useEffect(() => {
    setError(context.error);
  }, [context.error]);

  useEffect(() => {
    const currencies: string[] = [];
    const formats: FormatType[] = [];

    const selectedRowsIds = selectedRows.map((row) => row.seriesId);
    setCurrentMapping(mapping);

    const newSelections = mapping.columns.filter((col) => selectedRowsIds.includes(col.seriesId));
    setSelectedRows(newSelections);

    newSelections.forEach((r) => {
      currencies.push(r.currency);
      formats.push(determineFormat(r));
    });
    const ownerships = determineOwnershipState(availableOwnerships, newSelections, existingOwnerships);
    const currenciesSet = new Set(currencies);
    const formatsSet = new Set(formats);
    setBulkEditableProps({
      selectedCurrencies: currenciesSet,
      selectedFormats: formatsSet,
      selectedOwnerships: ownerships,
    });
    // Paweł: Do not add anything to the dependency array, or the app will go into an infinite rerender loop
    // We really want this effect to take place ONLY when we get an updated mapping
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapping]);

  useEffect(() => {
    setErrors(convertColumnErrorsToViewModels(errorColumns ?? []));
  }, [errorColumns]);

  const onBulkOwnershipChange = useCallback(
    (selectedMappings: ColumnMapping[], updatedOwnerships: DropMenuCheckboxItem[]): FileMapping => {
      const selectedRowIds = selectedMappings.map((m) => m.seriesId);
      const newColumns = currentMapping.columns.map((mapping) => {
        if (!selectedRowIds.includes(mapping.seriesId)) {
          return mapping;
        }

        const updatedIds = new Set<string>(mapping.ownerContextIds ?? []);
        updatedOwnerships.forEach((item) => {
          if (item.checked) {
            updatedIds.add(item.value);
          } else if (!item.indeterminate && !existingOwnerships[mapping.seriesId]?.includes(item.value)) {
            updatedIds.delete(item.value);
          }
        });
        return { ...mapping, ownerContextIds: Array.from(updatedIds) };
      });
      return { ...currentMapping, columns: newColumns };
    },
    [currentMapping, existingOwnerships],
  );

  const cellRendererParams: ReviewCellRendererProps = useMemo(
    () => ({
      currentMapping,
      errors,
      metadata,
      mode,
      onMappingChange,
      onBulkOwnershipChange,
      ownershipSelectorState,
      setOwnershipSelectorState,
      existingOwnerships,
      setExistingOwnerships,
      availableOwnerships,
      updateMappingOptimistically,
      additionalMetadata: additionalMetadata(currentMapping, metadata, mode, errors),
      editingSeriesId,
      setEditingSeriesId,
    }),
    [
      currentMapping,
      errors,
      metadata,
      mode,
      onMappingChange,
      onBulkOwnershipChange,
      ownershipSelectorState,
      setOwnershipSelectorState,
      availableOwnerships,
      existingOwnerships,
      setExistingOwnerships,
      updateMappingOptimistically,
      editingSeriesId,
      setEditingSeriesId,
    ],
  );

  const colDefs = useMemo(
    (): ColDef<ColumnMapping>[] => [
      {
        field: 'fundName',
        headerName: 'Name',
        flex: 1,
        cellRenderer: NameCellRenderer,
        cellRendererParams,
        cellClass: 'overflow-visible',
      },
      {
        width: 264,
        autoHeight: true,
        cellRenderer: SparklineWithFormatAndPerformanceCellRenderer,
        cellRendererParams,
      },
      {
        headerName: 'Currency',
        width: 80,
        cellRenderer: CurrencyCellRenderer,
        cellRendererParams,
      },
      ...(hasInvestmentOwnership
        ? [
            {
              headerName: 'Ownership',
              width: 175,
              cellRenderer: OwnershipCellRenderer,
              cellRendererParams,
            },
          ]
        : []),
      {
        headerName: 'Action',
        width: 175,
        cellRenderer: ActionCellRenderer,
        cellRendererParams,
      },
      {
        maxWidth: 30,
        cellRenderer: StatusCellRenderer,
        cellRendererParams,
      },
      {
        flex: 1,
        autoHeight: true,
        maxWidth: 93,
        cellRenderer: ErrorCellRenderer,
        cellRendererParams,
        // We need to override default AG Grid styles to make the button have full height
        cellClass: '[&>div]:h-full [&>div>span]:flex [&>div>span]:h-full pr-0',
      },
    ],
    [cellRendererParams, hasInvestmentOwnership],
  );

  const submitCorrections = useCallback(
    async (corrections: ErrorViewModel[]) => {
      const { fileId } = currentMapping;
      const { errors: newErrors, mapping: newMapping } = await fetchCorrectData(fileId, corrections);
      const validatedErrors = validateCells(convertColumnErrorsToViewModels(newErrors), corrections);
      setErrors(validatedErrors);
      setError('');
      setCurrentMapping(newMapping);
    },
    [currentMapping],
  );

  const updateErrors = useCallback(
    (error: ErrorViewModel, value: string) => {
      const updatedErrors = updateErrorValue(errors, error, value);
      submitCorrections(updatedErrors);
    },
    [errors, submitCorrections],
  );

  // errors list detail renderer params
  const detailCellRendererParams = useMemo(() => {
    return {
      errors,
      editingSeriesId,
      columns: currentMapping.columns,
      metadata,
      updateErrors,
      isPrice,
    };
  }, [currentMapping.columns, editingSeriesId, errors, metadata, updateErrors, isPrice]);

  const onContinueInternal = () => {
    if (ownershipSelectorState.rowIdForOwnershipSelector) {
      // sync optimistic ui mapping state with BE
      onMappingChange(mapping);
      setOwnershipSelectorState((prev: OwnershipSelectorState) => ({
        ...prev,
        rowIdForOwnershipSelector: '',
      }));
    }

    onContinue(mapping, errors);
  };

  const onBulkChange = (columns: ColumnMapping[], values: ColumnMappingMetadata) => {
    const newMapping = updateMappingColumnStateForNewReview(
      { mapping: currentMapping, columns, values },
      updateColumnMappingsForNewReviewByGlobalChange,
    );
    onMappingChange(newMapping);
  };

  const columnsToUpload = getColumnsToUpload(mapping.columns, metadata, errors);

  const uploadButtonLabel = customUploadReviewButtonLabel ?? 'Add to Library';

  return (
    <div className={cn('flex flex-col items-start flex-1 self-stretch overflow-hidden')}>
      {!!error && <ErrorMessage>{error}</ErrorMessage>}
      <div
        className={cn(
          'px-[24px] py-[8px] bg-venn-grey-10 border-b border-x-0 border-t-0 border-solid border-venn-grey-30 w-full',
        )}
      >
        Manage data format, currency, ownership, and actions by selecting one or more investments below.
      </div>
      <DataGridStyledWrapper>
        <DataGrid
          columnDefs={colDefs}
          defaultColDef={DEFAULT_COL_DEF}
          domLayout="normal"
          getRowId={(params: GetRowIdParams<ColumnMapping>) => params.data.seriesId}
          headerHeight={32}
          rowHeight={35}
          horizontalScrollbarHeight={15}
          rowData={currentMapping.columns}
          rowSelection={rowSelection}
          suppressContextMenu
          suppressCellFocus
          onSelectionChanged={(event) => {
            const selection: ColumnMapping[] = event.api.getSelectedRows();
            setSelectedRows(selection);
            const currencies = new Set<string>();
            const formats = new Set<FormatType>();
            const ownerships = determineOwnershipState(availableOwnerships, selection, existingOwnerships);
            selection.forEach((row) => {
              currencies.add(row.currency);
              formats.add(determineFormat(row));
            });
            setBulkEditableProps({
              selectedCurrencies: currencies,
              selectedFormats: formats,
              selectedOwnerships: ownerships,
            });
          }}
          masterDetail
          detailRowAutoHeight
          isRowMaster={() => true}
          detailCellRenderer={ErrorsListRenderer}
          detailCellRendererParams={detailCellRendererParams}
        />
      </DataGridStyledWrapper>
      <BulkInvestmentEditor
        selectedRows={selectedRows}
        cellRendererParams={cellRendererParams}
        bulkEditableProps={bulkEditableProps}
        onBulkChange={onBulkChange}
        onBulkOwnershipChange={onBulkOwnershipChange}
      />
      <UniversalUploaderFooter
        className="review-footer-btn-container"
        onCancel={onCancel}
        onStartOver={onStartOver}
        onContinue={onContinueInternal}
        disabled={loading}
        hasNoData={!columnsToUpload.length}
        isSample={isSample}
        primaryLabel={uploadButtonLabel}
      />
    </div>
  );
};

const DataGridStyledWrapper = styled.div`
  width: 100%;
  height: 100% !important;

  .ag-root {
    --ag-cell-horizontal-padding: 8px;
    --ag-font-size: 12px !important;

    --ag-border-color: ${GetColor.GreyScale.Grey40};
    --ag-row-hover-color: ${GetColor.GreyScale.Grey10};
    --ag-odd-row-background-color: ${GetColor.White};

    --ag-checkbox-checked-color: ${GetColor.DarkBlue};
    --ag-checkbox-indeterminate-color: ${GetColor.DarkBlue};

    --ag-selected-row-background-color: ${GetColor.DarkBlue}1A;
  }

  .ag-header {
    background-color: ${GetColor.GreyScale.Grey20};
  }

  .ag-header-cell {
    font-size: var(--ag-font-size);
  }

  .ag-column-first {
    padding-left: 24px;
    padding-right: 0;
  }

  .ag-column-last {
    padding-right: 24px;
  }
`;
